import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { formatCurrency, formatDate } from '@/lib/utils';
import { SerialHistoryLink } from '@/components/SerialHistoryLink';
import { Receipt, User, MapPin, ArrowCounterClockwise, FileText, DownloadSimple } from '@phosphor-icons/react';
import { billingAPI } from '@/lib/api';
import { toast } from 'sonner';

/**
 * Reusable Credit Note detail dialog (used by SalesCreditsPage + InvoicesPage admin CN tab).
 * Accepts a CN object (from billing_credit_notes) with line_items, reference_invoice_number, etc.
 */
export default function CreditNoteDetailDialog({ cn, open, onClose }) {
  if (!cn) return null;
  const cnNum = cn.cn_number || cn.creditnote_number;
  const refInv = cn.reference_invoice_number || cn.linked_invoice_number || cn.invoice_number;
  const customer = cn.customer_name || cn.customer?.name || '-';
  const lineItems = cn.line_items || [];
  const subtotal = cn.subtotal ?? 0;
  const tax = cn.total_tax ?? 0;
  const total = cn.grand_total ?? cn.total ?? 0;
  const balance = cn.balance ?? 0;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="cn-detail-dialog-shared">
        <DialogHeader>
          <DialogTitle className="font-['Plus_Jakarta_Sans'] flex items-center gap-2">
            <ArrowCounterClockwise size={20} className="text-red-500" />
            <span>{cnNum}</span>
            <span className={`text-xs px-2 py-0.5 rounded-full ml-2 ${cn.status === 'closed' ? 'bg-emerald-50 text-emerald-700' : cn.status === 'open' ? 'bg-amber-50 text-amber-700' : 'bg-gray-100 text-gray-600'}`}>
              {cn.status || '-'}
            </span>
            <button
              onClick={async () => { try { await billingAPI.adminViewCreditNotePdf(cn.id); } catch (e) { toast.error('Failed to open CN PDF'); } }}
              className="ml-auto inline-flex items-center gap-1.5 text-xs font-medium bg-[#0F4C81] text-white px-3 py-1.5 rounded-lg hover:bg-[#0B3C68] transition-colors"
              data-testid="cn-download-pdf-btn"
            >
              <DownloadSimple size={14} /> Download PDF
            </button>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2" data-testid="cn-detail-content">
          {/* Top summary cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-[#F8FAFC] rounded-lg p-3">
              <p className="text-[10px] uppercase tracking-wide text-[#64748B]">CN Date</p>
              <p className="text-sm font-medium">{formatDate(cn.cn_date || cn.date)}</p>
            </div>
            <div className="bg-[#F8FAFC] rounded-lg p-3">
              <p className="text-[10px] uppercase tracking-wide text-[#64748B]">Amount</p>
              <p className="text-sm font-bold text-red-600">{formatCurrency(total)}</p>
            </div>
            <div className="bg-[#F8FAFC] rounded-lg p-3">
              <p className="text-[10px] uppercase tracking-wide text-[#64748B]">Balance</p>
              <p className="text-sm font-medium">{formatCurrency(balance)}</p>
            </div>
            <div className="bg-[#F8FAFC] rounded-lg p-3">
              <p className="text-[10px] uppercase tracking-wide text-[#64748B]">Source</p>
              <p className="text-sm font-medium capitalize">{(cn.source || '-').replace(/_/g, ' ')}</p>
            </div>
          </div>

          {/* Customer + Reference invoice */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            <div className="border border-[#E2E8F0] rounded-lg p-3">
              <p className="text-[10px] uppercase tracking-wide text-[#64748B] flex items-center gap-1 mb-1"><User size={12} /> Customer</p>
              <p className="font-medium text-[#0F172A]">{customer}</p>
              {cn.customer_gstin && <p className="text-xs text-[#64748B]">GSTIN: {cn.customer_gstin}</p>}
              {cn.customer_phone && <p className="text-xs text-[#64748B]">Phone: {cn.customer_phone}</p>}
            </div>
            <div className="border border-[#E2E8F0] rounded-lg p-3">
              <p className="text-[10px] uppercase tracking-wide text-[#64748B] flex items-center gap-1 mb-1"><FileText size={12} /> Reference</p>
              <p className="font-medium text-[#0F172A]">
                {refInv ? <span className="font-mono text-[#0F4C81]">{refInv}</span> : <span className="text-[#64748B]">Standalone CN</span>}
              </p>
              {cn.reference_number && <p className="text-xs text-[#64748B]">Ref #: {cn.reference_number}</p>}
              {cn.clinic_name && <p className="text-xs text-[#64748B] flex items-center gap-1 mt-0.5"><MapPin size={10} />{cn.clinic_name}</p>}
            </div>
          </div>

          {/* Lead source / Salesperson / Audiologist (if present) */}
          {(cn.lead_source || cn.salesperson_name || cn.audiologist_name) && (
            <div className="grid grid-cols-3 gap-3 text-xs">
              {cn.lead_source && <div><span className="text-[#64748B]">Lead Source:</span> <strong>{cn.lead_source}</strong></div>}
              {cn.salesperson_name && <div><span className="text-[#64748B]">Salesperson:</span> <strong>{cn.salesperson_name}</strong></div>}
              {cn.audiologist_name && <div><span className="text-[#64748B]">Audiologist:</span> <strong>{cn.audiologist_name}</strong></div>}
            </div>
          )}

          {/* Line Items */}
          {lineItems.length > 0 && (
            <div className="border border-[#E2E8F0] rounded-lg overflow-hidden" data-testid="cn-line-items">
              <div className="bg-[#F8FAFC] px-3 py-2 border-b border-[#E2E8F0]">
                <h3 className="text-sm font-medium flex items-center gap-1"><Receipt size={14} />Line Items ({lineItems.length})</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-white text-[#64748B] border-b border-[#E2E8F0]">
                      <th className="text-left p-2 font-medium">Item</th>
                      <th className="text-left p-2 font-medium">HSN</th>
                      <th className="text-right p-2 font-medium">Qty</th>
                      <th className="text-right p-2 font-medium">Rate</th>
                      <th className="text-right p-2 font-medium">Disc</th>
                      <th className="text-right p-2 font-medium">Tax%</th>
                      <th className="text-right p-2 font-medium">Amount</th>
                      <th className="text-left p-2 font-medium">Serials</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lineItems.map((li, i) => (
                      <tr key={i} className="border-t border-[#E2E8F0]">
                        <td className="p-2 font-medium">{li.name || li.item_name || '-'}</td>
                        <td className="p-2 text-[#64748B] font-mono">{li.hsn_sac || li.hsn || '-'}</td>
                        <td className="p-2 text-right">{li.quantity ?? '-'}</td>
                        <td className="p-2 text-right">{formatCurrency(li.rate || 0)}</td>
                        <td className="p-2 text-right text-[#64748B]">{li.discount ? formatCurrency(li.discount_amount || li.discount) : '-'}</td>
                        <td className="p-2 text-right text-[#64748B]">{li.tax_percent ? `${li.tax_percent}%` : '-'}</td>
                        <td className="p-2 text-right font-medium">{formatCurrency(li.amount || li.line_amount || li.item_total || 0)}</td>
                        <td className="p-2">
                          {(li.serial_numbers || []).length > 0 ? (li.serial_numbers || []).map(sn => (
                            <SerialHistoryLink key={sn} serialNumber={sn} className="text-[10px] mr-1" />
                          )) : <span className="text-[#64748B]">-</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Linked Invoice Serials — fallback when per-line-item match not possible */}
          {(() => {
            const hasAnyLineSerials = lineItems.some(li => (li.serial_numbers || []).length > 0);
            const linked = cn.linked_invoice_serials || [];
            if (hasAnyLineSerials || linked.length === 0) return null;
            return (
              <div className="border border-emerald-200 bg-emerald-50 rounded-lg p-3" data-testid="cn-linked-invoice-serials">
                <p className="text-xs font-medium text-emerald-800 mb-2">
                  Serials from linked invoice {cn.linked_invoice_number ? <span className="font-mono">({cn.linked_invoice_number})</span> : null} — returned to stock by this CN
                </p>
                <div className="flex flex-wrap gap-1">
                  {linked.map((s, i) => (
                    <span key={i} className="inline-flex items-center gap-1 text-[10px] bg-white border border-emerald-200 rounded px-1.5 py-0.5">
                      <span className="font-mono text-emerald-700">{s.serial_number}</span>
                      {s.item_name && <span className="text-[#64748B]">— {s.item_name}</span>}
                    </span>
                  ))}
                </div>
              </div>
            );
          })()}

          {/* Totals */}
          <div className="bg-[#0F4C81] rounded-lg p-4 space-y-1 text-sm">
            <div className="flex justify-between text-emerald-200"><span>Subtotal</span><span>{formatCurrency(subtotal)}</span></div>
            <div className="flex justify-between text-emerald-200"><span>Tax</span><span>{formatCurrency(tax)}</span></div>
            <div className="flex justify-between text-white font-bold text-lg border-t border-emerald-700 pt-1 mt-1"><span>Credit Note Total</span><span className="text-red-300">{formatCurrency(total)}</span></div>
          </div>

          {/* Reason / Notes */}
          {(cn.reason || cn.notes) && (
            <div className="border border-orange-200 bg-orange-50 rounded-lg p-3 text-sm">
              {cn.reason && <div><span className="text-[#64748B] text-xs">Reason: </span><span className="text-[#0F172A]">{cn.reason}</span></div>}
              {cn.notes && <div className="mt-1"><span className="text-[#64748B] text-xs">Notes: </span><span className="text-[#0F172A]">{cn.notes}</span></div>}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
